import { isFalsy } from 'utility-types';
import type { PostFilterType } from '@/interfaces/component/filter.interface';
import {
  type AppStore,
  type AppStoreDto,
  type AppStorePostFilter,
  type ChannelAddress,
  type ChannelId,
  type ChannelName,
  type CrawlingResultStatusFailType,
  type PostLegalCareStatus,
  type PostLegalCareStatusFilters,
  type PostReplyFilter,
  type PostViolation
} from '@/interfaces/service/appstore/appstore.interface';
import type {
  BuisinessAddress,
  ChannelData,
  KakaoBuisinessSearchResultItem,
  ModoodocBuisinessSearchResultItem,
  NaverBuisinessSearchResultItem
} from '@/interfaces/service/appstore/appstore.search.interface';
import { baseAppStorePostFilter } from '@/stores/appstore.store';

export const getBuisinessNameNextPage = (query: string, prevQuery: string, channelName: ChannelName, channelData: ChannelData) => {
  if (query.trim() !== prevQuery.trim()) {
    prevQuery = query;
    return 1;
  }
  const channelDatum = channelData[channelName] ?? undefined;
  if (isFalsy(channelData) || isFalsy(channelDatum) || channelDatum?.isEnd) {
    return 1;
  }
  return channelDatum.nextPage;
};

export const addressConverter = <T extends Partial<KakaoBuisinessSearchResultItem & NaverBuisinessSearchResultItem & ModoodocBuisinessSearchResultItem>>(
  hospitalSearchResultItem: T,
  channelName: ChannelName
): BuisinessAddress => {
  const addressFromRes =
    hospitalSearchResultItem?.road_address_name ||
    hospitalSearchResultItem?.address ||
    hospitalSearchResultItem?.address_name ||
    hospitalSearchResultItem?.roadAddress ||
    hospitalSearchResultItem?.road_address_name ||
    '';

  const matchResult = addressFromRes?.match(/^(.*?(?:동.*?가|동))\s/) ?? undefined;
  const addressRemovedUnderDong = matchResult ? matchResult[1] : addressFromRes;

  const name = hospitalSearchResultItem?.name || hospitalSearchResultItem?.title || hospitalSearchResultItem?.place_name;

  /* 네이버에서 제공하는 주소데이터에는 id가 없음 */
  return {
    id: hospitalSearchResultItem?.id ?? `${hospitalSearchResultItem?.mapx}-${hospitalSearchResultItem?.mapy}`,
    name: name?.replace(/(<([^>]+)>)/gi, '') ?? '',
    address: channelName === 'KAKAO' ? addressFromRes : addressRemovedUnderDong
  };
};

export const getChannelKoreanName = (channelNameEn: ChannelName) => {
  switch (channelNameEn) {
    case 'KAKAO':
      return '카카오맵';
    case 'NAVER':
      return '네이버 플레이스';
    case 'MODOODOC':
      return '모두닥';
    case 'GOOGLE':
      return '구글 플레이스';
    case 'REVIEW_BOOST':
      return '리뷰 부스터';
    default:
      return '';
  }
};

export const getChannelLogoSrc = (channelNameEn: ChannelName) => {
  const rootPath = '/images/icons/';
  const suffix = '_btn_icons.png';
  switch (channelNameEn) {
    case 'KAKAO':
      return `${rootPath}kakaomap${suffix}`;
    case 'NAVER':
      return `${rootPath}naver${suffix}`;
    case 'MODOODOC':
      return `${rootPath}modoodoc${suffix}`;
    case 'GOOGLE':
      return `${rootPath}google${suffix}`;
    case 'REVIEW_BOOST':
      return `${rootPath}reviewboost${suffix}`;
    default:
      return '';
  }
};

export const getChannelKoreanNameById = (channelId: ChannelId | undefined) => {
  switch (channelId) {
    case 'KAKAO_MAP':
      return '카카오맵';
    case 'NAVER_MAP':
      return '네이버 플레이스';
    case 'MODOODOC':
      return '모두닥';
    case 'GOOGLE_MAP':
      return '구글 플레이스';
    case 'REVIEW_BOOST':
      return '리뷰 부스터';
    case 'NAVER_CAFE':
      return '네이버 카페';
    case 'GANGNAM_SISTER_MAP':
      return '강남언니 리뷰';
    case 'GANGNAM_SISTER_COMMUNITY':
      return '강남언니 커뮤니티';
    case 'BABITALK_FREETALK':
      return '바비톡 자유톡';
    case 'BABITALK_BALPOOM':
      return '바비톡 발품후기';
    case 'BABITALK_SISOOL':
      return '바비톡 시술후기';
    case 'YEOSHIN_TICKET':
      return '여신 티켓';
    case 'NAVER_BLOG':
      return '네이버 블로그';
    case 'YOUTUBE':
      return '유튜브';
    case 'CARROT_MARKET_STORE':
      return '당근마켓 스토어';
    default:
      return '전체 채널';
  }
};

export const getChannelLogoSrcById = (channelId: ChannelId | undefined) => {
  const rootPath = '/images/icons/';
  const suffix = '_btn_icon.png';
  const newRootPath = process.env.NEXT_PUBLIC_BASE_IMAGE_URL + '/channels/';
  switch (channelId) {
    case 'KAKAO_MAP':
      return `${rootPath}kakaomap${suffix}`;
    case 'NAVER_MAP':
      return `${rootPath}naver${suffix}`;
    case 'MODOODOC':
      return `${rootPath}modoodoc${suffix}`;
    case 'GOOGLE_MAP':
      return `${rootPath}google${suffix}`;
    case 'REVIEW_BOOST':
      return `${rootPath}reviewboost.svg`;
    case 'NAVER_CAFE':
      return `${rootPath}naver_cafe${suffix}`;
    case 'GANGNAM_SISTER_MAP':
    case 'GANGNAM_SISTER_COMMUNITY':
      return `${rootPath}gangnam_sister_map${suffix}`;
    case 'BABITALK_FREETALK':
    case 'BABITALK_BALPOOM':
    case 'BABITALK_SISOOL':
      return `${newRootPath}babitalk${suffix}`;
    case 'YEOSHIN_TICKET':
      return `${newRootPath}yeoshin-ticket${suffix}`;
    case 'NAVER_BLOG':
      return `${newRootPath}naver-blog${suffix}`;
    case 'YOUTUBE':
      return `${newRootPath}youtube${suffix}`;
    case 'CARROT_MARKET_STORE':
      return `${newRootPath}carrot-market-store${suffix}`;
    default:
      return '';
  }
};

export const getChannelFilterItems = (
  channels: ChannelAddress[],
  currentChannel = 'ALL'
): {
  name: string;
  value: ChannelId | string;
  selected: boolean;
  src: string;
}[] => {
  const channelIds = channels?.map((channel) => channel.channelId) ?? [];

  return ['ALL', ...channelIds].map((id) => {
    return {
      name: getChannelKoreanNameById(id as ChannelId),
      value: id as ChannelId | 'ALL',
      selected: currentChannel === id,
      src: getChannelLogoSrcById(id as ChannelId)
    };
  });
};

export const getReplyFilterLabel = (replyStatus: PostReplyFilter) => {
  switch (replyStatus) {
    case 'ALL':
      return '전체';
    case 'HAS_REPLY':
      return '답변 완료';
    case 'NO_REPLY':
      return '미답변';
    default:
      return '';
  }
};

export const getAppStorePostsViolationLabel = (violation: PostViolation) => {
  switch (violation) {
    case 'DEFAMATORY':
      return '명예훼손';
    case 'INSULTING':
      return '모욕';
    default:
      return '';
  }
};

export const getSelectedFilterValue = (currentFilter: AppStorePostFilter): PostFilterType => {
  if (!currentFilter) {
    return 'ALL';
  }

  const { replyFilter, reviewType, hasProduct, positiveWordList = [], negativeWordList = [] } = currentFilter;

  if (replyFilter === 'NO_REPLY' && reviewType === 'NEGATIVE' && hasProduct === false) {
    return 'NEGATIVE_POSTS_NO_ACTION';
  }
  if (hasProduct) {
    return 'NEGATIVE_POSTS_ACTION_PROGRESS';
  }
  if (replyFilter === 'NO_REPLY') {
    return 'NO_REPLY';
  }
  if (!isFalsy(reviewType)) {
    return reviewType === 'POSITIVE' ? 'POSITIVE_POSTS' : 'NEGATIVE_POSTS';
  }
  if (positiveWordList.length > 0) {
    return 'POSITIVE_WORDS';
  }
  if (negativeWordList.length > 0) {
    return 'NEGATIVE_WORDS';
  }
  return 'ALL';
};

export const convertLegalCareStatusToLegalCareStatusFilterValue = (status: PostLegalCareStatus | undefined | null): PostLegalCareStatusFilters | null => {
  switch (status) {
    case 'LAWYER_ASSIGNING':
    case 'LAWYER_REVIEWING':
      return 'ING';
    case 'COMPLETED':
      return 'COMPLETED';
    default:
      return null;
  }
};

export const getAppStorePostsLegalCareStautsLabel = (status: PostLegalCareStatusFilters) => {
  switch (status) {
    case 'ING':
      return '진행중';
    case 'COMPLETED':
      return '진행완료';
    default:
      return '';
  }
};

export const getMainAddressOfAppStore = (AppStore: AppStore | AppStoreDto): ChannelAddress => {
  return AppStore.channels.find((channel) => channel.channelId === 'NAVER_MAP') ?? AppStore.channels[0];
};

export const setBasePropsAppStore = (baseAppStore: AppStoreDto): AppStore => {
  return {
    ...baseAppStore,
    filter: baseAppStorePostFilter,
    sortOption: 'LATEST'
  };
};

export const getSubmitReplyFailMessages = (failType: CrawlingResultStatusFailType, channelId: ChannelId): { title: string; desc: string } => {
  let channelName = '계정';

  if (channelId === 'NAVER_MAP') {
    channelName = `네이버 ${channelName}`;
  } else if (channelId === 'KAKAO_MAP') {
    channelName = `카카오톡 ${channelName}`;
  } else if (channelId === 'MODOODOC') {
    channelName = `모두닥 ${channelName}`;
  }

  switch (failType) {
    case 'ID_PASSWORD_ERROR':
      return {
        title: `아이디 또는 비밀번호가 잘못되었습니다.`,
        desc: '네이버 플레이스 관리 권한이 있는\n아이디와 비밀번호를 정확히 입력해 주세요.'
      };
    case 'SAFETY_LOCK_ERROR':
      return {
        title: `보호 조치가 확인되었습니다.`,
        desc: `${channelName}에 직접 로그인하여\n보호조치를 해제한 후 다시 시도해 주세요.`
      };
    // case 'CAPTCHA_ERROR':
    //   return {
    //     title: `로그인에 실패하였습니다.`,
    //     desc: '시스템 오류로 인해 로그인이 실패하였습니다.\n문제 원인을 확인한 후 빠르게 연락드리도록 하겠습니다.\n이용에 불편을 드린 점 진심으로 사과드립니다.'
    //   };
    case 'NONE_PLACE_ERROR':
      return {
        title: `장소에 대한 권한이 없습니다.`,
        desc: '로그인한 계정이 해당 장소에 대한 권한을 가지고 있지 않습니다. \n올바른 계정으로 로그인해 주세요.'
      };
    case 'AUTHENTICATION_ERROR':
      return {
        title: `인증이 실패했습니다.`,
        desc: '2차 인증에 실패했습니다.'
      };
    default:
      return {
        title: `시스템 에러`,
        desc: '시스템 에러로 인해 처리하지 못했습니다.\n문제가 지속될 경우 고객센터로 문의해주세요.'
      };
  }
};
